import { Controller, useFormContext } from 'react-hook-form'
import Select, {
  CSSObjectWithLabel,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  StylesConfig,
  components,
} from "react-select";
import { Label } from "../ui/label";
import { OptionType } from "../../../app/utils/optionSelectType";
import { BasicInput } from "../BasicInput";
import { focusNextElement } from "@/app/utils/focusNextElement";
import { cn } from "@/app/utils/cn";

interface BasicSelectProps {
  label?: string | React.ReactNode;
  name: string;
  options: OptionType[];
  placeholder: string;
  styles?: StylesConfig<OptionType, false, GroupBase<OptionType>>;
  error?: string;
  disabled?: boolean;
  tabIndex?: number;
  onInputChange?: (inputValue: string) => void;
  className?: string;
  classNameLabel?: string;
  hiddeError?: boolean;
  classNameContainer?: string;
}
const customStyles = {
  control: (
    base: CSSObjectWithLabel,
    props: ControlProps<OptionType, false, GroupBase<OptionType>>,
  ): CSSObjectWithLabel => ({
    ...base,
    borderRadius: '6px',
    border: props.isFocused ? '2px solid black' : '1px solid hsl(var(--input))',
    boxShadow: 'black',
    '&:hover': {
      border: '1px solid hsl(var(--input))',
    },
    backgroundColor: 'transparent',
  }),
  placeholder: (base: CSSObjectWithLabel) => ({
    ...base,
    color: 'black',
  }),
}
export const BasicSelect = ({
  name,
  label,
  options,
  placeholder,
  styles = customStyles,
  error,
  disabled = false,
  tabIndex,
  onInputChange,
  className,
  classNameLabel,
  hiddeError = false,
  classNameContainer,
}: BasicSelectProps) => {
  const { control } = useFormContext()

  const DropdownIndicator = (
    props: DropdownIndicatorProps<OptionType, false, GroupBase<OptionType>>,
  ) => {
    return (
      <components.DropdownIndicator {...props}>
        <i className="fa-solid fa-chevron-down text-black"></i>
      </components.DropdownIndicator>
    )
  }

  return (
    <div className={cn("h-[66px] w-full", classNameContainer)}>
      <Label htmlFor={name} className={cn("w-full", classNameLabel)}>
        {label}
        {disabled ? (
          <BasicInput name={name} disabled />
        ) : (
          <Controller
            control={control}
            name={name}
            render={({ field: { value, onChange } }) => (
              <Select
                options={options}
                value={options.find((option) => option.value === value) || null}
                onChange={(option) => onChange(option?.value ?? '')}
                onInputChange={(inputValue) => {
                  if (onInputChange) {
                    onInputChange(inputValue || '')
                  }
                }}
                placeholder={placeholder}
                id={name}
                className={cn("mt-1 w-full", className)}
                styles={styles}
                components={{
                  IndicatorSeparator: () => null,
                  DropdownIndicator,
                }}
                tabIndex={tabIndex}
                onKeyDown={(e) => {
                  if (e.key === 'Tab') {
                    e.preventDefault()
                    e.stopPropagation()
                    focusNextElement(e.currentTarget)
                  }
                }}
              />
            )}
          />
        )}
        {!hiddeError && (
          <p className="mt-1 min-h-5 font-light text-error">{error}</p>
        )}
      </Label>
    </div>
  )
}
